import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Backdrop,
  Fade,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LoadingButton } from "@mui/lab";
import { useState, useEffect } from "react";
import axios from "axios";

const PackageSettingsModal = ({
  open,
  onClose,
  selectedPackage,
  setSelectedPackage,
  customerSelectedRow,
  currentSession,
  // Document handlers
  handleSendDocument,
  handleDownloadCustomerDocs,
  sendDocOwnerLoading,
  sendDocContractorLoading,
  documentDonwloadLoading,
  downloadError,
  emailSentMessage,
  emailSentError,
}) => {
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [selectedSignOrder, setSelectedSignOrder] = useState("contractor");
  const [originalSignOrder, setOriginalSignOrder] = useState("contractor");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [modifyLoading, setModifyLoading] = useState(false);
  const [signOrderMessage, setSignOrderMessage] = useState({
    type: "",
    text: "",
  });

  // Get original sign order from package
  useEffect(() => {
    if (selectedPackage) {
      const signOrder =
        selectedPackage?.processing_permit?.notarization?.signing_order ||
        "contractor";
      setOriginalSignOrder(signOrder);
      setSelectedSignOrder(signOrder);
    }
  }, [selectedPackage]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setAccordionExpanded(false);
      if (selectedPackage) {
        const signOrder =
          selectedPackage?.processing_permit?.notarization?.signing_order ||
          "contractor";
        setSelectedSignOrder(signOrder);
      }
    }
  }, [open, selectedPackage]);

  const handleAccordionChange = (event, isExpanded) => {
    setAccordionExpanded(isExpanded);
  };

  const handleSignOrderChange = (event) => {
    setSelectedSignOrder(event.target.value);
  };

  const handleModifyClick = () => {
    setConfirmModalOpen(true);
  };

  const handleConfirmModify = async () => {
    try {
      setModifyLoading(true);
      setSignOrderMessage({ type: "", text: "" });

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/change-sign-order`,
        {
          task_id: selectedPackage?.task_id,
          signing_order: selectedSignOrder,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        }
      );

      if (response?.data?.success) {
        // Update the selected package with the new data from the response
        if (response?.data?.data && setSelectedPackage) {
          setSelectedPackage(response.data.data);
        }
        // After successful modification, update originalSignOrder
        setOriginalSignOrder(selectedSignOrder);
        setAccordionExpanded(false);
        setConfirmModalOpen(false);
        setSignOrderMessage({
          type: "success",
          text: response?.data?.message || "Sign order changed successfully",
        });
        // Clear message after 3 seconds
        setTimeout(() => setSignOrderMessage({ type: "", text: "" }), 3000);
      } else {
        // Handle error - show error message
        setSignOrderMessage({
          type: "error",
          text: response?.data?.message || "Failed to modify sign order",
        });
        setConfirmModalOpen(false);
      }
    } catch (error) {
      console.error("Error modifying sign order:", error);
      setSignOrderMessage({
        type: "error",
        text:
          error?.response?.data?.message ||
          "An error occurred while modifying sign order",
      });
      setConfirmModalOpen(false);
    } finally {
      setModifyLoading(false);
    }
  };

  const handleCancelModify = () => {
    setConfirmModalOpen(false);
  };

  const hasSignOrderChanged = selectedSignOrder !== originalSignOrder;

  // Check all conditions for disabling accordion and get appropriate message
  const getAccordionDisabledState = () => {
    const notarization = selectedPackage?.processing_permit?.notarization;
    const contractor = notarization?.contractor?.notarization;

    // Check if processing_permit is empty or has no keys
    if (
      !selectedPackage?.processing_permit ||
      Object.keys(selectedPackage.processing_permit || {}).length === 0
    ) {
      return {
        disabled: true,
        message: "Notarization/eSign has not been started yet by the user.",
      };
    }

    // Check if noc_affidavit document exists - owner must sign first
    const documents = selectedPackage?.documents || {};
    if (documents.noc_affidavit) {
      return {
        disabled: true,
        message:
          "Cannot change sign order: NOC Affidavit requires owner to sign first.",
      };
    }

    // Check if finalized
    if (notarization?.finalized === true) {
      return {
        disabled: true,
        message: "Notarization has been finalized and cannot be modified.",
      };
    }

    // Check if not started
    if (notarization?.started === false) {
      return {
        disabled: true,
        message: "Notarization has not been started yet.",
      };
    }

    // Check contractor conditions
    if (contractor?.is_notarized === true) {
      return {
        disabled: true,
        message:
          "Contractor has already been notarized. Sign order cannot be changed.",
      };
    }

    if (contractor?.has_notary === true) {
      return {
        disabled: true,
        message:
          "Contractor has been assigned a notary. Sign order cannot be changed.",
      };
    }

    if (contractor?.bn_id && contractor.bn_id !== "") {
      return {
        disabled: true,
        message:
          "Contractor has a BlueNotary ID assigned. Sign order cannot be changed.",
      };
    }

    if (
      contractor?.appointments?.booked &&
      Array.isArray(contractor.appointments.booked) &&
      contractor.appointments.booked.length > 0
    ) {
      return {
        disabled: true,
        message:
          "Contractor has appointments booked. Sign order cannot be changed.",
      };
    }

    // Check owner conditions (similar to contractor)
    const owner = notarization?.owner?.notarization;
    if (owner?.is_notarized === true) {
      return {
        disabled: true,
        message:
          "Owner has already been notarized. Sign order cannot be changed.",
      };
    }

    if (owner?.has_notary === true) {
      return {
        disabled: true,
        message:
          "Owner has been assigned a notary. Sign order cannot be changed.",
      };
    }

    if (owner?.bn_id && owner.bn_id !== "") {
      return {
        disabled: true,
        message:
          "Owner has a BlueNotary ID assigned. Sign order cannot be changed.",
      };
    }

    if (
      owner?.appointments?.booked &&
      Array.isArray(owner.appointments.booked) &&
      owner.appointments.booked.length > 0
    ) {
      return {
        disabled: true,
        message: "Owner has appointments booked. Sign order cannot be changed.",
      };
    }

    // If none of the conditions are met, accordion is enabled
    return {
      disabled: false,
      message: null,
    };
  };

  const accordionState = getAccordionDisabledState();
  return (
    <>
      <Modal
        aria-labelledby="package-settings-modal-title"
        aria-describedby="package-settings-modal-description"
        open={open}
        onClose={
          documentDonwloadLoading ||
          sendDocOwnerLoading ||
          sendDocContractorLoading
            ? () => {}
            : onClose
        }
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: 600,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Typography
                id="package-settings-modal-title"
                variant="h5"
                fontWeight="bold"
                color="primary"
              >
                Package Settings
              </Typography>
              <IconButton
                disabled={
                  documentDonwloadLoading ||
                  sendDocOwnerLoading ||
                  sendDocContractorLoading
                }
                aria-label="Close package settings modal"
                onClick={onClose}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Documents Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, color: "text.primary" }}
              >
                Documents
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Error/Success Messages */}
              {(downloadError || emailSentMessage || emailSentError) && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={
                      downloadError || emailSentError ? "error" : "success"
                    }
                    align="center"
                  >
                    {downloadError || emailSentMessage || emailSentError}
                  </Typography>
                </Box>
              )}

              {/* Send Documents Buttons */}
              <Box
                gap={2}
                sx={{
                  mb: 2,
                  display: "flex",
                  gap: 1,
                }}
              >
                <LoadingButton
                  onClick={() => handleSendDocument("owner")}
                  loading={sendDocOwnerLoading}
                  disabled={documentDonwloadLoading || sendDocContractorLoading}
                  size="small"
                  variant="contained"
                  fullWidth
                >
                  Send Documents to Property Owner
                </LoadingButton>
                <LoadingButton
                  onClick={() => handleSendDocument("contractor")}
                  loading={sendDocContractorLoading}
                  disabled={documentDonwloadLoading || sendDocOwnerLoading}
                  size="small"
                  variant="contained"
                  fullWidth
                >
                  Send Documents to Contractor
                </LoadingButton>
              </Box>

              {/* Download Documents Button */}
              <Box>
                <LoadingButton
                  loading={documentDonwloadLoading}
                  disabled={sendDocOwnerLoading || sendDocContractorLoading}
                  onClick={handleDownloadCustomerDocs}
                  size="small"
                  variant="outlined"
                  fullWidth
                >
                  Download Documents
                </LoadingButton>
              </Box>
            </Box>

            {/* Notarization/eSign Section */}
            <Box>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, color: "text.primary" }}
              >
                Notarization/eSign
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Change Notarization/Sign order Accordion */}
              <Accordion
                expanded={accordionExpanded}
                onChange={handleAccordionChange}
                disabled={accordionState.disabled}
                sx={{ mb: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="sign-order-content"
                  id="sign-order-header"
                >
                  <Typography variant="body1" fontWeight="bold">
                    Change Notarization/Sign order
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      row
                      aria-label="signing-order"
                      name="signing-order"
                      value={selectedSignOrder}
                      onChange={handleSignOrderChange}
                      sx={{ justifyContent: "flex-start", gap: 2 }}
                    >
                      <FormControlLabel
                        value="owner"
                        control={<Radio />}
                        label="Owner"
                      />
                      <FormControlLabel
                        value="contractor"
                        control={<Radio />}
                        label="Contractor"
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Modify Button - enabled when selection differs from original */}
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Button
                      onClick={handleModifyClick}
                      variant="contained"
                      color="primary"
                      disabled={!hasSignOrderChanged}
                    >
                      Modify
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Success/Error message for sign order change */}
              {signOrderMessage.text && (
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={
                    signOrderMessage.type === "success" ? "success" : "error"
                  }
                  sx={{ mb: 2, ml: 1 }}
                >
                  {signOrderMessage.text}
                </Typography>
              )}

              {/* Helper text when accordion is disabled */}
              {accordionState.disabled && accordionState.message && (
                <Typography
                  variant="caption"
                  color="warning"
                  sx={{ mt: -1.8, mb: 2, ml: 1, display: "block" }}
                >
                  {accordionState.message}
                </Typography>
              )}
            </Box>

            {/* Close Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 4,
              }}
            >
              <Button
                disabled={
                  documentDonwloadLoading ||
                  sendDocOwnerLoading ||
                  sendDocContractorLoading
                }
                variant="outlined"
                onClick={onClose}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Confirmation Dialog - Outside Modal to avoid nesting issues */}
      <Dialog
        open={confirmModalOpen}
        onClose={modifyLoading ? () => {} : handleCancelModify}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Sign Order Change
        </DialogTitle>
        <DialogContent>
          <Typography id="confirm-dialog-description">
            Are you sure you want to change the notarization/sign order to{" "}
            <strong>
              {selectedSignOrder === "owner" ? "Owner" : "Contractor"}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelModify}
            color="inherit"
            disabled={modifyLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleConfirmModify}
            variant="contained"
            color="primary"
            autoFocus
            loading={modifyLoading}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PackageSettingsModal;
